import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Mail, Send, Loader2, MessageSquare, User, Building2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { showError, showSuccess } from '@/lib/toast';

interface FormState {
  name: string;
  email: string;
  company: string;
  subject: string;
  message: string;
}

const initialState: FormState = {
  name: '',
  email: '',
  company: '',
  subject: '',
  message: '',
};

export function ContactSection() {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>(initialState);
  const [submitting, setSubmitting] = useState(false);

  const subjectOptions = [
    { value: '', label: t('landing.contact.subjectPlaceholder') },
    { value: 'general', label: t('landing.contact.subjects.general') },
    { value: 'support', label: t('landing.contact.subjects.support') },
    { value: 'sales', label: t('landing.contact.subjects.sales') },
    { value: 'partnership', label: t('landing.contact.subjects.partnership') },
    { value: 'feedback', label: t('landing.contact.subjects.feedback') },
  ];

  const update = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Friendly client-side checks. Backend validates again.
    if (!form.name.trim() || !form.email.trim() || !form.subject || !form.message.trim()) {
      showError(t('landing.contact.errors.required'));
      return;
    }
    if (form.message.trim().length < 10) {
      showError(t('landing.contact.errors.messageTooShort'));
      return;
    }

    setSubmitting(true);
    try {
      // Send the display label (e.g. "General inquiry") instead of the internal key
      // so the support inbox gets a readable subject line without extra mapping.
      const subjectLabel =
        subjectOptions.find((o) => o.value === form.subject)?.label || form.subject;

      await api.post('/contact', {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || undefined,
        subject: subjectLabel,
        message: form.message.trim(),
      });

      showSuccess(t('landing.contact.success'));
      setForm(initialState);
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: { message?: string | string[] } };
        message?: string;
      };
      if (axiosErr?.response?.status === 429) {
        showError(t('landing.contact.errors.rateLimited'));
      } else {
        const backendMessage = axiosErr?.response?.data?.message;
        const msg = Array.isArray(backendMessage)
          ? backendMessage.join(', ')
          : backendMessage || axiosErr?.message || t('landing.contact.errors.generic');
        showError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 relative overflow-hidden">
      {/* Ambient background — matches CTASection / site palette */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-emerald-50/30 dark:via-emerald-950/10 to-background" />
        <motion.div
          className="absolute top-20 left-1/4 w-96 h-96 rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(16, 185, 129, 0.12) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
          animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-20 right-1/4 w-80 h-80 rounded-full"
          style={{
            background:
              'radial-gradient(circle, rgba(20, 184, 166, 0.10) 0%, transparent 70%)',
            filter: 'blur(60px)',
          }}
          animate={{ scale: [1.1, 1, 1.1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />
      </div>

      <div className="container mx-auto px-4 relative">
        {/* Header */}
        <motion.div
          className="text-center max-w-2xl mx-auto mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-sm font-semibold mb-4">
            <Sparkles className="w-4 h-4" />
            {t('landing.contact.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            {t('landing.contact.titleStart')}{' '}
            <span className="bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
              {t('landing.contact.titleHighlight')}
            </span>
          </h2>
          <p className="text-muted-foreground text-lg">
            {t('landing.contact.subtitle')}
          </p>
        </motion.div>

        {/* Form card */}
        <motion.div
          className="max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <form
            onSubmit={handleSubmit}
            className="relative rounded-2xl border border-border bg-background/60 backdrop-blur-sm p-6 md:p-10 shadow-xl shadow-emerald-500/5"
          >
            <div className="grid md:grid-cols-2 gap-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {t('landing.contact.name')} <span className="text-emerald-500">*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <input
                    type="text"
                    value={form.name}
                    onChange={update('name')}
                    placeholder={t('landing.contact.namePlaceholder')}
                    required
                    maxLength={100}
                    disabled={submitting}
                    className="w-full rounded-lg border bg-background pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-1.5">
                  {t('landing.contact.email')} <span className="text-emerald-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <input
                    type="email"
                    value={form.email}
                    onChange={update('email')}
                    placeholder="your@email.com"
                    required
                    maxLength={200}
                    disabled={submitting}
                    className="w-full rounded-lg border bg-background pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60"
                  />
                </div>
              </div>
            </div>

            {/* Company (full width, optional) */}
            <div className="mt-5">
              <label className="block text-sm font-medium mb-1.5">
                {t('landing.contact.company')}{' '}
                <span className="text-xs text-muted-foreground font-normal">
                  ({t('landing.contact.optional')})
                </span>
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  value={form.company}
                  onChange={update('company')}
                  placeholder={t('landing.contact.companyPlaceholder')}
                  maxLength={150}
                  disabled={submitting}
                  className="w-full rounded-lg border bg-background pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60"
                />
              </div>
            </div>

            {/* Subject */}
            <div className="mt-5">
              <label className="block text-sm font-medium mb-1.5">
                {t('landing.contact.subject')} <span className="text-emerald-500">*</span>
              </label>
              <select
                value={form.subject}
                onChange={update('subject')}
                required
                disabled={submitting}
                className="w-full rounded-lg border bg-background px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-60"
              >
                {subjectOptions.map((opt) => (
                  <option key={opt.value} value={opt.value} disabled={opt.value === ''}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Message */}
            <div className="mt-5">
              <label className="block text-sm font-medium mb-1.5">
                {t('landing.contact.message')} <span className="text-emerald-500">*</span>
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
                <textarea
                  value={form.message}
                  onChange={update('message')}
                  placeholder={t('landing.contact.messagePlaceholder')}
                  required
                  rows={6}
                  minLength={10}
                  maxLength={5000}
                  disabled={submitting}
                  className="w-full rounded-lg border bg-background pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none disabled:opacity-60"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1 text-right">
                {form.message.length} / 5000
              </p>
            </div>

            {/* Submit + contact line */}
            <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <a
                href="mailto:support@setopenclaw.com"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-emerald-600 transition-colors"
              >
                <Mail className="h-4 w-4" />
                support@setopenclaw.com
              </a>
              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="group gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/25 font-semibold px-8"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('landing.contact.sending')}
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    {t('landing.contact.send')}
                  </>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
